/**
 * Sample HTML content template
 * @type {string}
 */
const sampleHtmlContent = `
<h1>Sample Content</h1>
<p>This is a sample content for the page frame component.</p>
<ul>
    <li>Item 1</li>
    <li>Item 2</li>
    <li>Item 3</li>
</ul>
`;

/**
 * QdsPageFrame Web Component
 * 
 * A lightweight component that renders HTML content within a configurable frame.
 * This component allows setting width and height constraints and displays
 * HTML content within these constraints.
 * 
 * @element qds-page-frame
 * 
 * @attr {String} html-content - HTML content to display in the frame
 * @attr {String} width - Width of the frame (CSS value)
 * @attr {String} height - Height of the frame (CSS value)
 * 
 * @property {String} htmlContent - Get/set the HTML content
 * @property {String} width - Get/set the width
 * @property {String} height - Get/set the height
 */
class QdsPageFrame extends HTMLElement {
    private _htmlContent: string;
    private _width: string;
    private _height: string;

    /**
     * Creates an instance of QdsPageFrame
     */
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this._htmlContent = sampleHtmlContent;
        this._width = "1024px";
        this._height = "100%";
    }

    /**
     * Specifies the attributes to observe for changes
     * @returns {string[]} Array of attribute names to observe
     */
    static get observedAttributes(): string[] {
        return ['html-content', 'width', 'height'];
    }

    /**
     * Get the HTML content
     * @returns {string} Current HTML content
     */
    get htmlContent(): string {
        return this._htmlContent;
    }

    /**
     * Set the HTML content and trigger re-render
     * @param {string} value - New HTML content
     */
    set htmlContent(value: string) {
        this._htmlContent = value;
        this.render();
    }

    /**
     * Get the width value
     * @returns {string} Current width
     */
    get width(): string {
        return this._width;
    }

    /**
     * Set the width value and trigger re-render
     * @param {string} value - New width
     */
    set width(value: string) {
        this._width = value;
        this.render();
    }

    /**
     * Get the height value
     * @returns {string} Current height
     */
    get height(): string {
      return this._height;
    }

    /**
     * Set the height value and trigger re-render
     * @param {string} value - New height
     */
    set height(value: string) {
        this._height = value;
        this.render();
    }

    /**
     * Responds to attribute changes
     * 
     * @param {string} name - Name of the attribute that changed
     * @param {string} oldValue - Previous value of the attribute
     * @param {string} newValue - New value of the attribute
     */
    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        if (name === 'html-content') {
            this.htmlContent = newValue;
        } else if (name === 'width') {
            this.width = newValue;
        } else if (name === 'height') {
            this.height = newValue;
        }
    }

    /**
     * Called when the element is inserted into the DOM
     */
    connectedCallback(): void {
        if (!this.isConnected) return;
        this.render();
    }

    /**
     * Renders the component's HTML structure
     */
    private render(): void {
        if (!this.shadowRoot) return;
        
        this.shadowRoot.innerHTML = `
        <style>
            :host {
                display: block;
                width: 100%;
                height: auto;
                min-height: 44px; /* Touch target size */
            }
            
            .frame-container {
                width: 100%;
                max-width: ${this.escapeHtml(this.width)};
                max-height: ${this.escapeHtml(this.height)};
                overflow: auto;
                border: 1px solid var(--theme-border-primary);
                border-radius: 4px;
                padding: 10px;
                box-sizing: border-box;
                background-color: var(--theme-background-primary);
                color: var(--theme-text-primary);
            }
        </style>
        <div id="frame-container" class="frame-container">${this.escapeHtml(this.htmlContent || "")}</div>
    `;
    }

    /**
     * Escapes HTML to prevent XSS
     * Note: The actual escaping is currently commented out
     * 
     * @param {string} unsafe - Potentially unsafe HTML string
     * @returns {string} Processed HTML string
     */
    private escapeHtml(unsafe: string): string {
        return unsafe.replace(/[&<"']/g, function (m) {
            switch (m) {
                //case '&': return '&amp;';
                //case '<': return '&lt;';
                //case '"': return '&quot;';
                //case "'": return '&#39;';
                default: return m;
            }
        });
    }
}

customElements.define('qds-page-frame', QdsPageFrame);
